// Verbatim problems shared by practitioners in Lab0 discovery calls.
// Extracted from interviews; previously featured on the landing page.
export const featuredQuotes = [
  {
    theme: 'Value leakage',
    signal: '3-4 months gone',
    role: 'VP Professional Services, data platform',
    quote:
      'By the time the customer starts using it, 3-4 months have gone by. They paid for a year; for 400k they have not got any value.',
  },
  {
    theme: 'Customer blockers',
    signal: 'firewalls, BRDs, infra',
    role: 'Implementation director, enterprise CX suite',
    quote:
      'Most delays are not from the vendor side. Firewalls that cannot get opened, BRDs that cannot be shared, infra that was not provisioned before purchase.',
  },
  {
    theme: 'Data cleanup',
    signal: '100 platforms',
    role: 'Implementation lead, HRIS platform',
    quote:
      'The data we need is sitting in 100 different platforms, and there is no common sync between those data lines.',
  },
  {
    theme: 'Knowledge debt',
    signal: 'not captured',
    role: 'Former delivery lead, learning platform',
    quote:
      'Implementation knowledge stays with the field delivery person, not captured anywhere.',
  },
]

export const discoveryQuotes = [
  ...featuredQuotes,
  {
    theme: 'Adoption risk',
    signal: 'hard to deploy',
    role: 'VP Professional Services, data platform',
    quote:
      'It is happening because of lack of product adoption. And why? Because the product is hard to deploy.',
  },
  {
    theme: 'Early decisions',
    signal: 'dragging the project',
    role: 'Implementation director, enterprise CX suite',
    quote:
      'The wrong person made a wrong decision early, and it is dragging the whole project.',
  },
  {
    theme: 'Data dependency',
    signal: '40-50% delay',
    role: 'Head of professional services, enterprise software',
    quote:
      'Give me the data and I will roll this out in two weeks. These bottlenecks account for 40-50% of implementation delays.',
  },
  {
    theme: 'Timeline stretch',
    signal: '3 to 12 months',
    role: 'Independent implementation consultant',
    quote:
      'Enterprise implementations go up from 3 months to 6 months to 12 months in some cases.',
  },
  {
    theme: 'Customer response',
    signal: '50% of delays',
    role: 'Independent implementation consultant',
    quote:
      'Around 50% of delays are due to customers not providing required information on time.',
  },
  {
    theme: 'Requirement capture',
    signal: '10 questions',
    role: 'Former delivery lead, learning platform',
    quote:
      'Requirements gathering is the most boring part of the job. I have a script of 10 standard questions I ask every client.',
  },
  {
    theme: 'Artifact factory',
    signal: 'manual docs',
    role: 'Former delivery lead, learning platform',
    quote:
      'A lot of time my team spends creating different documents and artifacts: training manuals, guides, videos.',
  },
  {
    theme: 'Identity mismatch',
    signal: 'manual resolution',
    role: 'Implementation lead, HRIS platform',
    quote:
      'Name in one system: l.gupta. Name in payroll: lakshya.g. These discrepancies must be manually resolved.',
  },
  {
    theme: 'Schema fit',
    signal: 'cleanup is the work',
    role: 'Implementation lead, HRIS platform',
    quote:
      'Uploading data is not difficult. Cleaning that data so it fits the target schema is where all the time goes.',
  },
  {
    theme: 'Human and technical',
    signal: 'two bottlenecks',
    role: 'Migration consultant, security platform',
    quote:
      'Two types of bottleneck. One because of humans: approvals, bureaucracy. Second is technical: data migration, cleaning, backup.',
  },
  {
    theme: 'Why drift',
    signal: 'sales promised everything',
    role: 'Migration consultant, security platform',
    quote:
      'The management itself does not know why they are moving. The sales team just pitched everything.',
  },
  {
    theme: 'Ownership drag',
    signal: 'people own the delay',
    role: 'Enterprise consultant, Australia',
    quote:
      'In 4 sprints, 8 weeks, you should discover everything. But the people who own that are the ones slowing it down.',
  },
  {
    theme: 'Diagnosis blur',
    signal: 'people, process, governance, tech',
    role: 'Enterprise consultant, Australia',
    quote:
      'Is it a people issue? Is it a process issue? Is it a governance issue? Is it a technology issue?',
  },
  {
    theme: 'Executive blocking',
    signal: '2-3 weeks idle',
    role: 'ERP systems integrator',
    quote:
      'A senior walks in, ego takes over, chaos ensues. We cannot predict when a senior will block a decision.',
  },
  {
    theme: 'Custom work',
    signal: '30-40% extra effort',
    role: 'ERP systems integrator',
    quote:
      'There is no standardisation. Every client is a custom build. That is 30-40% extra effort.',
  },
  {
    theme: 'Sign-off drag',
    signal: '15-20 days',
    role: 'ERP systems integrator',
    quote: 'Sign-off cycles cause 15-20 day delays.',
  },
  {
    theme: 'Deployment drag',
    signal: 'months',
    role: 'Growth automation platform leader',
    quote: 'Deployment time is too much today. It is in months.',
  },
  {
    theme: 'Services burden',
    signal: '$70-80k on $100k',
    role: 'Growth automation platform leader',
    quote:
      'Professional services cost can equal or exceed the SaaS license: $70-80k consulting on a $100k deal.',
  },
  {
    theme: 'Migration rehearsal',
    signal: '45 days for 23 hours',
    role: 'Warehouse automation platform leader',
    quote:
      'One migration cycle: 45 days with repeated testing across local, staging, prod. Actual migration took 22-23 hours.',
  },
  {
    theme: 'Downtime risk',
    signal: 'forced rollback',
    role: 'Warehouse automation platform leader',
    quote:
      'A migration failed inside a 24-hour downtime window and forced rollback.',
  },
  {
    theme: 'Alignment work',
    signal: 'dev is not the bottleneck',
    role: 'AI services founder',
    quote:
      'Biggest time sinks: BRD, Jira stories, alignment, UAT. Dev and QA are not the bottleneck; documentation and alignment are.',
  },
  {
    theme: 'Requirement churn',
    signal: '1 becomes A',
    role: 'Customer experience platform leader',
    quote:
      'Requirements evolve from 1.0 to 1.1, 1.2, but sometimes completely change direction. That causes massive rework.',
  },
  {
    theme: 'Meeting summaries',
    signal: 'no next action',
    role: 'Customer experience platform leader',
    quote:
      'Meeting AI tools stop at summaries. They tell you what happened but do not tell you what to do next.',
  },
  {
    theme: 'Role sequencing',
    signal: '4 handoffs',
    role: 'Migration strategy team',
    quote:
      'Each migration requires four distinct roles in sequence: discovery, architect, implementation, validator.',
  },
  {
    theme: 'Retention',
    signal: 'time to value',
    role: 'Professional services leader, B2B SaaS',
    quote:
      'Speed of implementation is directly related to how fast the customer can see value and to retention metrics.',
  },
  {
    theme: 'Leadership push',
    signal: 'shared bandwidth',
    role: 'Professional services leader, B2B SaaS',
    quote:
      'Their bandwidth is shared. Someone has to prioritize this project, and that takes leadership push.',
  },
  {
    theme: 'Knowledge base',
    signal: 'right article, right step',
    role: 'Knowledge leader, backup platform',
    quote:
      'The knowledge base is misorganized. It needs to surface the right article at the right step.',
  },
  {
    theme: 'Cloud cost',
    signal: 'sales estimate blows up',
    role: 'Founder, cloud operations company',
    quote:
      'Cloud cost estimates are rough guesses in sales pitch; actuals blow up during implementation.',
  },
  {
    theme: 'POC handoff',
    signal: 'context lost',
    role: 'Founder, cloud operations company',
    quote:
      'POC-to-PS handoff needs full context preserved: what was promised, what data was used, what edge cases were found.',
  },
  {
    theme: 'Enterprise timelines',
    signal: '3 to 12 months',
    role: 'Professional services lead, identity platform',
    quote:
      'For a complex product and a big org, it could go from anywhere from three to six to maybe twelve months.',
  },
  {
    theme: 'Handoff loss',
    signal: 'paid for one thing',
    role: 'Professional services lead, identity platform',
    quote:
      'Lot of losses at handoffs... presales says customer paid for something, but now they want something else.',
  },
  {
    theme: 'Tool sprawl',
    signal: 'one pane missing',
    role: 'Professional services lead, identity platform',
    quote:
      'We have RocketLane, SFDC, Gong, Power BI... I try to bring all that into SFDC.',
  },
  {
    theme: 'Discovery gap',
    signal: 'not optimized',
    role: 'Head of professional services, enterprise software',
    quote:
      'Discovery is something we are not able to optimize. Testing - we spend roughly 30% of time.',
  },
  {
    theme: 'PS cost',
    signal: '$200-$400 per hour',
    role: 'Customer success executive, backup platform',
    quote:
      'PS is expensive, customers pay $200-$400 an hour... we are always understaffed.',
  },
  {
    theme: 'On-prem reality',
    signal: '80% on-prem',
    role: 'Customer success executive, backup platform',
    quote: "This world is 80% on-prem... can't give me cloud-only solution.",
  },
  {
    theme: 'Reusable work',
    signal: '80% same',
    role: 'ERP systems integrator',
    quote: '80% same agent, 20% configuration might be different.',
  },
  {
    theme: 'SI cashflow',
    signal: '120 days',
    role: 'ERP systems integrator',
    quote: 'I have to pay this guy for 120 days from my pocket.',
  },
  {
    theme: 'Integration speed',
    signal: '2 weeks vs 8',
    role: 'ERP systems integrator',
    quote:
      "If we could do a 70% integration in 2 weeks instead of 6-8 weeks, we'd win more deals.",
  },
  {
    theme: 'Adoption',
    signal: 'first use case',
    role: 'Professional services lead, event streaming platform',
    quote: 'First adoption is the most important part of professional services delivery.',
  },
  {
    theme: 'ROI attention',
    signal: 'loses interest',
    role: 'Professional services lead, event streaming platform',
    quote: "If customer doesn't see ROI on first use case, he starts to lose interest.",
  },
  {
    theme: 'Firewall cycle',
    signal: 'one month',
    role: 'Professional services lead, event streaming platform',
    quote:
      'Big banks... just to get one firewall rule, the cycle is almost a month.',
  },
  {
    theme: 'Artifact work',
    signal: '12-20 hours',
    role: 'Implementation manager, learning platform',
    quote:
      'Over a 30-day project cycle, people spend 12 to 20 hours building artifacts.',
  },
  {
    theme: 'Automation incentive',
    signal: '2,000 people',
    role: 'Implementation leader, enterprise CX platform',
    quote:
      'PS head with 2,000 people under him has no incentive to automate those people away. Control measured by how many people report to you.',
  },
  {
    theme: 'Sales to delivery',
    signal: 'independent conversations',
    role: 'Implementation leader, enterprise CX platform',
    quote: 'Sales and implementation cycles are completely independent conversations.',
  },
  {
    theme: 'KT delay',
    signal: '1 month intake',
    role: 'Field delivery lead, AI services company',
    quote:
      'Getting KT from client takes around a month... feedback cycles 1-2 months additional.',
  },
  {
    theme: 'Lost value',
    signal: 'still pay',
    role: 'Field consultant, backup platform',
    quote: "If they buy the product and we can't implement, they still pay - they lose value.",
  },
  {
    theme: 'Implementation churn',
    signal: 'lose a customer',
    role: 'Former head of PS and CS, enterprise SaaS',
    quote: 'Implementation is the phase where you lose a customer... it starts there.',
  },
  {
    theme: 'Headcount pressure',
    signal: '10 to 2 engineers',
    role: 'Former head of PS and CS, enterprise SaaS',
    quote: "If I'm able to solve with two engineers instead of ten... company would be happy.",
  },
  {
    theme: 'North star',
    signal: 'TTV/TTR',
    role: 'Head of implementation, customer experience platform',
    quote:
      'Time to value (TTV/TTR) is the north star metric for PSA and implementation teams.',
  },
  {
    theme: 'Unclear requirement',
    signal: 'unknown problem',
    role: 'Head of implementation, customer experience platform',
    quote:
      "Requirement itself is not clear. How would you solve it when you don't know problem?",
  },
  {
    theme: 'Next step gap',
    signal: 'not in AI tools',
    role: 'Head of implementation, customer experience platform',
    quote:
      'What really would take time... what is my logical next step? Have not seen this in any AI tool.',
  },
  {
    theme: 'Status tax',
    signal: '25 updates/week',
    role: 'Founder, AI adoption platform',
    quote: '25 status updates per week; Friday morning goes away in that.',
  },
  {
    theme: 'Communication drag',
    signal: 'context from last week',
    role: 'Founder, AI adoption platform',
    quote:
      'Communication takes a lot of overhead... gather Loom videos, context from last week, prepare report, send to champion.',
  },
  {
    theme: 'Long memory',
    signal: '3 months later',
    role: 'Field delivery engineer, education AI services',
    quote: 'A customer can ask something three months later which he said on day one.',
  },
  {
    theme: 'Context load',
    signal: '4 projects',
    role: 'Field delivery engineer, education AI services',
    quote:
      'I know all things about all 4 projects. How will an AI have that much context?',
  },
  {
    theme: 'Human in loop',
    signal: 'high ticket',
    role: 'Field delivery engineer, education AI services',
    quote:
      "I would say don't automate client-facing aspects for now, especially for high ticket products.",
  },
  {
    theme: 'Infosec drag',
    signal: '3-4 months',
    role: 'Field delivery engineer, AI employees platform',
    quote:
      'There was a lot of infosec that lasted around 3-4 months because they have a lot of security clearances.',
  },
  {
    theme: 'Human touch',
    signal: 'deployment oversight',
    role: 'Field delivery engineer, AI employees platform',
    quote:
      'People would still want someone there when it is getting deployed. The human touch is still required.',
  },
  {
    theme: 'Context disappearance',
    signal: 'human leaves',
    role: 'SaaS professional services leader',
    quote: 'When a human leaves, context disappears.',
  },
  {
    theme: 'Private AI',
    signal: 'AI agents kicked out',
    role: 'Managed services leader, cloud migrations',
    quote:
      'It is very funny because we are all working on AI technologies, but they do not want to use AI technologies.',
  },
  {
    theme: 'Discovery compression',
    signal: '50% efficiency',
    role: 'Managed services leader, cloud migrations',
    quote:
      "If we can bring the discovery phase down to seven to eight days, there's a 50% increase in efficiency.",
  },
  {
    theme: 'Migration complexity',
    signal: 'moving parts',
    role: 'Migration services lead, cloud platform',
    quote: "Migrations are pretty tricky, and there's a lot of moving parts.",
  },
  {
    theme: 'Manual migration',
    signal: 'step by step',
    role: 'Migration services lead, cloud platform',
    quote:
      "There's not a standardized path... we have to go step-by-step... a lot of manual steps in migrations.",
  },
  {
    theme: 'Limited access',
    signal: 'PDF bill',
    role: 'Migration services lead, cloud platform',
    quote:
      'Not all customers are happy giving us read-only access; we often just get a PDF bill and reverse-engineer.',
  },
  {
    theme: 'Engineer resistance',
    signal: 'another integration',
    role: 'Professional services lead, mobile SDK platform',
    quote:
      "It's hard to sell it to them... they see it as another integration they have to do.",
  },
  {
    theme: 'Claude objection',
    signal: 'why so slow',
    role: 'Founder, SI automation company',
    quote:
      "Their VPs were using Claude Code to build some demo apps... 'why, if we can build it, then why so much time in getting these tools implemented?'",
  },
  {
    theme: 'QA burden',
    signal: '30 QAs',
    role: 'Founder, SI automation company',
    quote: 'The QA is the largest workforce... there are a lot of QAs, almost like 30 QAs.',
  },
  {
    theme: 'Enterprise cost',
    signal: '$5M project',
    role: 'Founder, SI automation company',
    quote: 'The client spent almost 5 million USD on this one.',
  },
  {
    theme: 'Deployment target',
    signal: 'one month',
    role: 'Compliance services partner',
    quote:
      'If we can reduce the deployment cycle to a month, we can serve more clients and stay competitive.',
  },
]
